import { formatMoney, type Locale, pickLocale } from '@bb/shared';
import { createFileRoute, Link } from '@tanstack/react-router';
import { DateTime } from 'luxon';
import { Trans, useTranslation } from 'react-i18next';
import { useCurrentUser } from '@/features/auth/use-current-user';
import { SubscriberOnly, useIsSubscriber } from '@/features/billing/subscriber-only';
import {
  useDisconnectPayments,
  usePaymentsAccount,
  useRefreshPayments,
  useTransactions,
} from '@/features/booking/api';
import { btnPrimary, btnSecondary } from '@/lib/ui';

interface PaymentsSearch {
  connected?: string;
  error?: string;
}

export const Route = createFileRoute('/app/booking/payments')({
  validateSearch: (s: Record<string, unknown>): PaymentsSearch => ({
    ...(s.connected ? { connected: '1' } : {}),
    ...(typeof s.error === 'string' ? { error: s.error } : {}),
  }),
  component: PaymentsPage,
});

function PaymentsPage() {
  const { connected, error } = Route.useSearch();
  const { t, i18n } = useTranslation(['booking', 'common', 'errors']);
  const { data: user } = useCurrentUser();
  const { data: account, isLoading } = usePaymentsAccount();
  const isSubscriber = useIsSubscriber();
  const refresh = useRefreshPayments();
  const disconnect = useDisconnectPayments();
  const { data: tx } = useTransactions(Boolean(account?.connected));
  const locale = i18n.language as Locale;
  const zone = user?.timezone ?? 'UTC';

  if (isLoading || !account)
    return <p className="text-sm text-neutral-500">{t('common:status.loading')}</p>;

  return (
    <div className="max-w-2xl space-y-6">
      <p className="text-sm text-neutral-600">{t('booking:payments.help')}</p>

      {/*
        Connecting and being able to charge are not the same event. This used
        to promise "you can now add prices" the moment the account came back,
        including when Stripe had it on hold pending their own checks, which
        sent the host looking for a price field that would refuse to save.
      */}
      {connected ? (
        <p
          className={`rounded border px-3 py-2 text-sm ${
            account.chargesEnabled
              ? 'border-green-200 bg-green-50 text-green-800'
              : 'border-amber-200 bg-amber-50 text-amber-900'
          }`}
        >
          {account.chargesEnabled ? (
            t('booking:payments.connectedFlash')
          ) : (
            <Trans
              i18nKey="booking:payments.connectedPendingFlash"
              components={pendingLinks(account.dashboardUrl, () => refresh.mutate())}
            />
          )}
        </p>
      ) : null}
      {error ? (
        <p
          role="alert"
          className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
        >
          {t(`errors:${error}`)}
        </p>
      ) : null}

      <section className="rounded-lg border border-neutral-200 bg-white p-5">
        {!account.configured ? (
          <p className="text-sm text-neutral-500">{t('booking:payments.notConfigured')}</p>
        ) : !account.connected ? (
          <>
            <h2 className="text-base font-medium">{t('booking:payments.connectTitle')}</h2>
            <p className="mt-1 text-sm text-neutral-600">{t('booking:payments.connectBody')}</p>
            {isSubscriber ? (
              <a href="/api/v1/payments/connect" className={`${btnPrimary} mt-4 inline-block`}>
                {t('booking:payments.connect')}
              </a>
            ) : (
              <div className="mt-4">
                <SubscriberOnly feature={t('booking:payments.featureName')} />
              </div>
            )}
          </>
        ) : (
          <>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="text-base font-medium">{t('booking:payments.connectedTitle')}</h2>
                <p className="mt-1 text-sm text-neutral-600">
                  {account.accountId} · {account.country ?? '—'} · {account.defaultCurrency ?? '—'}
                  {account.livemode === false ? ` · ${t('booking:payments.testMode')}` : ''}
                </p>
              </div>
              <div className="flex flex-wrap gap-1.5">
                <Badge
                  ok={account.chargesEnabled}
                  label={t(
                    account.chargesEnabled
                      ? 'booking:payments.chargesEnabled'
                      : 'booking:payments.chargesPending',
                  )}
                />
                <Badge
                  ok={account.payoutsEnabled}
                  label={t(
                    account.payoutsEnabled
                      ? 'booking:payments.payoutsEnabled'
                      : 'booking:payments.payoutsPending',
                  )}
                />
              </div>
            </div>
            {/* The flash above has just said this, so don't say it twice. */}
            {!account.chargesEnabled && !connected ? (
              <p className="mt-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                <Trans
                  i18nKey="booking:payments.chargesDisabled"
                  components={pendingLinks(account.dashboardUrl, () => refresh.mutate())}
                />
              </p>
            ) : null}
            <div className="mt-4 flex flex-wrap gap-2">
              {account.dashboardUrl ? (
                <a
                  href={account.dashboardUrl}
                  target="_blank"
                  rel="noreferrer"
                  className={btnSecondary}
                >
                  {t('booking:payments.openDashboard')}
                </a>
              ) : null}
              <button
                type="button"
                onClick={() => refresh.mutate()}
                disabled={refresh.isPending}
                className={btnSecondary}
              >
                {t('booking:payments.refresh')}
              </button>
              <button
                type="button"
                onClick={() => {
                  if (window.confirm(t('booking:payments.disconnectConfirm'))) disconnect.mutate();
                }}
                disabled={disconnect.isPending}
                className={`${btnSecondary} text-red-700`}
              >
                {t('booking:payments.disconnect')}
              </button>
            </div>
          </>
        )}
      </section>

      {account.connected && tx ? (
        <section>
          <div className="mb-2 flex items-baseline justify-between">
            <h2 className="text-sm font-medium">{t('booking:payments.transactions')}</h2>
            {tx.totals.currency ? (
              <span className="text-xs text-neutral-500">
                {t('booking:payments.totals', {
                  paid: formatMoney(
                    { amount: tx.totals.paid, currency: tx.totals.currency },
                    locale,
                  ),
                  refunded: formatMoney(
                    { amount: tx.totals.refunded, currency: tx.totals.currency },
                    locale,
                  ),
                })}
              </span>
            ) : null}
          </div>
          {tx.items.length === 0 ? (
            <p className="rounded-lg border border-dashed border-neutral-300 p-5 text-sm text-neutral-600">
              {t('booking:payments.noTransactions')}
            </p>
          ) : (
            <ul className="divide-y divide-neutral-100 rounded-lg border border-neutral-200 bg-white">
              {tx.items.map((item) => (
                <li key={item.bookingId}>
                  <Link
                    to="/app/booking/bookings/$id"
                    params={{ id: item.bookingId }}
                    className="flex items-center gap-3 px-4 py-3 text-sm hover:bg-neutral-50 sm:py-2.5"
                  >
                    {/*
                      DATE_MED, not DATE_MED_WITH_WEEKDAY as the holiday list
                      uses: "Mon, Oct 12, 2026" is 119px and this column is
                      nowrap. The weekday is not what anyone scans a transaction
                      list for anyway; the point is only to stop 10/12/2026 being
                      read as 10 December.

                      w-28 rather than w-24, because a date with the month in
                      words is wider than 10/12/2026 was — and widest in
                      Japanese, where 2026年10月12日 measures 106px against the old
                      column's 96px. Measured in the browser with this app's font
                      stack, not guessed: w-28 (112px) clears the widest Japanese
                      date by a few pixels, and English sits at 83px.
                    */}
                    <span className="hidden w-28 shrink-0 whitespace-nowrap text-neutral-500 sm:block">
                      {DateTime.fromISO(item.paidAt ?? item.start, { zone })
                        .setLocale(locale)
                        .toLocaleString(DateTime.DATE_MED)}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium">{item.bookerName}</span>
                      <span className="block truncate text-xs text-neutral-500">
                        <span className="sm:hidden">
                          {DateTime.fromISO(item.paidAt ?? item.start, { zone })
                            .setLocale(locale)
                            .toLocaleString(DateTime.DATE_MED)}{' '}
                          ·{' '}
                        </span>
                        {pickLocale(item.eventTypeName, locale)}
                      </span>
                    </span>
                    <span className="shrink-0 text-right">
                      <span className="block tabular-nums">
                        {formatMoney({ amount: item.amount, currency: item.currency }, locale)}
                      </span>
                      {item.refundedAmount > 0 ? (
                        <span className="block text-xs text-neutral-500">
                          −
                          {formatMoney(
                            { amount: item.refundedAmount, currency: item.currency },
                            locale,
                          )}
                        </span>
                      ) : null}
                    </span>
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${item.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-neutral-200 text-neutral-700'}`}
                    >
                      {t(`booking:bookings.payment.${item.status}`)}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : null}
    </div>
  );
}

/**
 * The label has to carry the state, not just the field name.
 *
 * Both badges used to read "Charges enabled" whatever the answer was, with
 * only the colour saying otherwise, so an account Stripe had not approved
 * announced itself as ready. Somebody reasonably took that at face value and
 * went looking for the price field.
 */
/**
 * The two things a host with an unapproved account actually needs, as links
 * inside the sentence that mentions them rather than as instructions to go
 * and find a button elsewhere on the page.
 *
 * `status` re-asks Stripe in place, because the answer changing is the whole
 * point of pressing it.
 */
function pendingLinks(dashboardUrl: string | null, onCheck: () => void) {
  return {
    dash: dashboardUrl ? (
      // biome-ignore lint/a11y/useAnchorContent: Trans fills the content in
      <a href={dashboardUrl} target="_blank" rel="noreferrer" className="underline" />
    ) : (
      <span />
    ),
    status: <button type="button" onClick={onCheck} className="underline" />,
  };
}

function Badge({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-xs font-medium ${ok ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}
    >
      {ok ? '\u2713 ' : ''}
      {label}
    </span>
  );
}
